import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import API from "@/lib/api";
import { getErrorMessage } from "@/lib/errorHandler";
import { toast } from "sonner";
import {
  Building2,
  Users,
  FileText,
  Edit2,
  Check,
  X,
  TrendingUp,
  Crown,
  Upload,
  Trash2,
  Link,
  Copy,
} from "lucide-react";

export default function OrganizationSettings() {
  const { organization, isSuperadmin } = useAuth();
  const [orgDetails, setOrgDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoFileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    logo_url: "",
  });

  // Copy login link to clipboard
  const copyLoginLink = () => {
    if (!orgDetails?.slug) return;

    // Use new clean URL format: /org-slug/login
    const loginLink = `${window.location.origin}/${orgDetails.slug}/login`;
    navigator.clipboard.writeText(loginLink)
      .then(() => {
        toast.success("Login link copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy link");
      });
  };

  // Load organization details
  const loadOrganizationDetails = async () => {
    if (!organization?.id) return;

    setLoading(true);
    try {
      // For superadmins, use the full endpoint; for regular admins, use their org data from context
      if (isSuperadmin) {
        const res = await API.get(`/superadmin/organizations/${organization.id}`);
        setOrgDetails(res.data);
        setFormData({
          logo_url: res.data.logo_url || "",
        });
      } else {
        // Regular admins: use organization data from AuthContext
        // Fetch fresh data to get stats
        const res = await API.get(`/auth/me`);
        if (res.data.organization) {
          setOrgDetails(res.data.organization);
          setFormData({
            logo_url: res.data.organization.logo_url || "",
          });
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load organization details"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizationDetails();
  }, [organization]);

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, SVG, or WebP');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);

    try {
      // Delete old logo from S3 if exists
      if (formData.logo_url) {
        try {
          await API.delete('/upload/image', { params: { url: formData.logo_url } });
        } catch (err) {
          console.warn('Failed to delete old logo:', err);
        }
      }

      // Upload new logo
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await API.post('/upload/image?folder_type=logos', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = res.data.url;
      setFormData(prev => ({ ...prev, logo_url: imageUrl }));
      toast.success('Logo uploaded successfully!');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to upload logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Remove logo
  const removeLogo = async () => {
    if (!formData.logo_url) return;

    try {
      await API.delete('/upload/image', { params: { url: formData.logo_url } });
      setFormData(prev => ({ ...prev, logo_url: "" }));
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
      toast.success('Logo removed successfully!');
    } catch (err) {
      console.error('Failed to delete logo:', err);
      setFormData(prev => ({ ...prev, logo_url: "" }));
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
      toast.warning('Logo removed, but may still exist in storage');
    }
  };

  // Update organization
  const handleUpdate = async () => {
    try {
      // Use different endpoints based on user role
      if (isSuperadmin) {
        await API.put(`/superadmin/organizations/${organization.id}`, {
          logo_url: formData.logo_url || null,
        });
      } else {
        // Regular org admins use /api/organizations/me
        await API.put(`/organizations/me`, {
          logo_url: formData.logo_url || null,
        });
      }

      toast.success("Organization updated successfully");
      setEditDialog(false);
      loadOrganizationDetails();

      // Reload page to update logo in sidebar
      window.location.reload();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update organization"));
    }
  };

  // Calculate usage percentages
  const getUsagePercentage = (current, max) => {
    if (!max) return 0; // Unlimited
    return Math.min((current / max) * 100, 100);
  };

  // Get plan badge variant
  const getPlanBadgeVariant = (plan) => {
    switch (plan) {
      case "enterprise":
        return "default";
      case "pro":
        return "secondary";
      case "free":
        return "outline";
      default:
        return "outline";
    }
  };

  if (!organization) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">
            No Organization
          </h2>
          <p className="text-slate-500">
            {isSuperadmin
              ? "You are a superadmin and don't belong to an organization."
              : "You are not assigned to an organization yet."}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-500">Loading...</div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organization Settings</h1>
            <p className="text-slate-500 mt-1">
              Manage your organization settings and view usage statistics
            </p>
          </div>
          <Button onClick={() => setEditDialog(true)}>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Organization
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Organization Details */}
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Basic information about your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Organization Name</Label>
                <p className="text-lg font-medium">{orgDetails?.name}</p>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-slate-500">URL Slug</Label>
                <code className="text-sm bg-slate-100 px-2 py-1 rounded block mt-1">
                  {orgDetails?.slug}
                </code>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-slate-500">Organization Login Link</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-slate-100 px-3 py-2 rounded flex-1 truncate">
                      {window.location.origin}/{orgDetails?.slug}/login
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyLoginLink}
                      className="shrink-0"
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Share this link with your organization members to login with branded experience
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-slate-500">Plan</Label>
                <div className="mt-1">
                  <Badge variant={getPlanBadgeVariant(orgDetails?.plan)} className="text-sm">
                    <Crown className="w-3 h-3 mr-1" />
                    {orgDetails?.plan?.toUpperCase()}
                  </Badge>
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <div className="mt-1">
                  <Badge variant={orgDetails?.is_active ? "default" : "secondary"}>
                    {orgDetails?.is_active ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </>
                    ) : (
                      <>
                        <X className="w-3 h-3 mr-1" />
                        Suspended
                      </>
                    )}
                  </Badge>
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-slate-500">Created On</Label>
                <p className="text-sm text-slate-700 mt-1">
                  {new Date(orgDetails?.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Plan & Usage */}
          <Card>
            <CardHeader>
              <CardTitle>Plan & Usage</CardTitle>
              <CardDescription>Current usage and plan limits</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Admins */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <Label className="text-sm font-medium">Admins</Label>
                  </div>
                  <span className="text-sm text-slate-600">
                    {orgDetails?.admin_count || 0}
                    {orgDetails?.max_admins && ` / ${orgDetails.max_admins}`}
                  </span>
                </div>
                {orgDetails?.max_admins && (
                  <Progress
                    value={getUsagePercentage(
                      orgDetails?.admin_count || 0,
                      orgDetails?.max_admins
                    )}
                    className="h-2"
                  />
                )}
              </div>

              <Separator />

              {/* Quizzes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <Label className="text-sm font-medium">Quizzes</Label>
                  </div>
                  <span className="text-sm text-slate-600">
                    {orgDetails?.quiz_count || 0}
                    {orgDetails?.max_quizzes
                      ? ` / ${orgDetails.max_quizzes}`
                      : " / Unlimited"}
                  </span>
                </div>
                {orgDetails?.max_quizzes && (
                  <Progress
                    value={getUsagePercentage(
                      orgDetails?.quiz_count || 0,
                      orgDetails?.max_quizzes
                    )}
                    className="h-2"
                  />
                )}
                {!orgDetails?.max_quizzes && (
                  <p className="text-xs text-slate-500 mt-1">Unlimited quizzes available</p>
                )}
              </div>

              <Separator />

              {/* Users/Members */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <Label className="text-sm font-medium">Members</Label>
                  </div>
                  <span className="text-sm text-slate-600">
                    {orgDetails?.user_count || 0}
                    {orgDetails?.max_users
                      ? ` / ${orgDetails.max_users}`
                      : " / Unlimited"}
                  </span>
                </div>
                {orgDetails?.max_users && (
                  <Progress
                    value={getUsagePercentage(
                      orgDetails?.user_count || 0,
                      orgDetails?.max_users
                    )}
                    className="h-2"
                  />
                )}
                {!orgDetails?.max_users && (
                  <p className="text-xs text-slate-500 mt-1">Unlimited members allowed</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Edit Organization Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Organization</DialogTitle>
              <DialogDescription>
                Update organization settings (limited options for admins)
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Organization Logo</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Upload a logo image (JPG, PNG, GIF, SVG, WebP - Max 5MB)
                </p>

                {formData.logo_url ? (
                  <div className="relative inline-block max-w-fit">
                    <img
                      src={formData.logo_url}
                      alt="Logo preview"
                      className="h-20 object-contain border border-slate-200 rounded-md p-2 bg-white"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors z-10"
                      title="Remove logo"
                    >
                      ×
                    </button>
                  </div>
                ) : null}

                <div className="flex gap-2">
                  <input
                    ref={logoFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="org-logo-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoFileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="flex-1"
                  >
                    {uploadingLogo ? (
                      <>
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {formData.logo_url ? 'Change Logo' : 'Upload Logo'}
                      </>
                    )}
                  </Button>
                </div>

                <Separator className="my-2" />

                <Label htmlFor="logo_url">Or enter logo URL manually</Label>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) =>
                    setFormData({ ...formData, logo_url: e.target.value })
                  }
                  placeholder="https://example.com/logo.png"
                />

                <p className="text-xs text-slate-500 mt-2">
                  Contact support to change organization name, plan, or limits
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}
